import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "cerulean";
import { Row, ContentCard, Button, ResponsiveFlex } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useShowUserNeedsApprovalAlert } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { calculateBulkTotals } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import styles from "../../transfer/ach/ReceiptScreen.module.scss";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useACHImportContext } from "../ACHImportContext";
import { useTemplateContext } from "./templates/TemplateContext";

export const ReceiptBulkScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const { ach_payments_collect: achPaymentsCollectFeature } = useUserFeatures();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { showAlert } = useShowUserNeedsApprovalAlert("ach");
  const { resetForm, values } = form;
  const { templateQueryString, isTemplateMode, excludedRows } =
    useTemplateContext();
  const { importType, clearImport } = useACHImportContext();
  const isImportMode = importType !== null;
  const { payments: allPayments } = values;
  const payments = useMemo(
    () =>
      isTemplateMode
        ? allPayments.filter((_, i) => !excludedRows.has(i))
        : allPayments,
    [allPayments, isTemplateMode, excludedRows]
  );
  const {
    sendTotalFormatted,
    netTotalFormattedWithSign,
    collectTotalFormatted,
  } = calculateBulkTotals(payments);
  const totalSendPayments = payments.filter(
    (p) => p.transactionType === "send"
  ).length;
  const totalCollectPayments = payments.filter(
    (p) => p.transactionType === "collect"
  ).length;

  const handleClickDone = () => {
    navigate(
      isImportMode
        ? `/${PAYMENT_ACTIVITY_ROUTE}/file-import`
        : `/${PAYMENT_ACTIVITY_ROUTE}/bulk`
    );
  };

  const handleClickCTA = () => {
    resetForm();
    clearImport();
    navigate(
      isImportMode
        ? `/${PAYMENT_ACTIVITY_ROUTE}/upload`
        : `..${templateQueryString}`
    );
  };

  useEffect(() => {
    if (showAlert) {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          "ach-payment-wire-approval-org-requires-users",
          null,
          "Your submission can’t be approved until another user has approval permissions."
        ),
      });
    }
  }, [showAlert]);

  const SendOnlyRows = () => (
    <>
      <Row>
        <Row.Item>
          <p>{l10n.getString("label-number-payments")}</p>
        </Row.Item>
        <Row.Item shrink>
          <p>{payments.length}</p>
        </Row.Item>
      </Row>
      <Row>
        <Row.Item>
          <p>{l10n.getString("label-total-amount")}</p>
        </Row.Item>
        <Row.Item shrink>
          <p>{sendTotalFormatted}</p>
        </Row.Item>
      </Row>
      <Row>
        <Row.Item>
          <p>{l10n.getString("label-delivery-speed")}</p>
        </Row.Item>
        <Row.Item shrink>
          <p>{l10n.getString("ach-payment-receipt-delivery")}</p>
        </Row.Item>
      </Row>
    </>
  );

  const CollectAndSendRows = () => (
    <>
      {totalSendPayments > 0 ? (
        <Row>
          <Row.Item>
            <p>{l10n.getString("label-sending")}</p>
          </Row.Item>
          <Row.Item shrink>
            <p>{`${sendTotalFormatted} ${l10n.getString("via-payments-cta", {
              total: totalSendPayments,
            })}`}</p>
          </Row.Item>
        </Row>
      ) : null}
      {totalCollectPayments > 0 ? (
        <Row>
          <Row.Item>
            <p>{l10n.getString("label-collecting")}</p>
          </Row.Item>
          <Row.Item shrink>
            <p>{`${collectTotalFormatted} ${l10n.getString("via-payments-cta", {
              total: totalCollectPayments,
            })}`}</p>
          </Row.Item>
        </Row>
      ) : null}
      <Row>
        <Row.Item>
          <p>{l10n.getString("label-net-total")}</p>
        </Row.Item>
        <Row.Item shrink>
          <p>{netTotalFormattedWithSign}</p>
        </Row.Item>
      </Row>
    </>
  );

  return (
    <div className={styles.receiptContainer}>
      <ContentCard>
        <div className={styles.cardContainer}>
          <h2 className="fontColor--heading">
            {achDualApprovalRequired
              ? l10n.getString("ach-payment-receipt-dual-approval-title")
              : l10n.getString("bulk-payment-submitted-header")}
            <i className="narmi-icon-check margin--left--xs" />
          </h2>
          <div className={styles.contentContainer}>
            <ResponsiveFlex gapSize="l">
              {achPaymentsCollectFeature ? (
                <CollectAndSendRows />
              ) : (
                <SendOnlyRows />
              )}
            </ResponsiveFlex>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              label={l10n.getString("label-done")}
              testId="ach-bulk-payment-receipt-done-button"
              onClick={handleClickDone}
            />
            <Button
              kind="secondary"
              label={l10n.getString("ach-payment-receipt-cta")}
              testId="ach-bulk-payment-receipt-cta-button"
              onClick={handleClickCTA}
            />
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default ReceiptBulkScreen;
